import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Bug, Lightbulb, Sparkles, Heart, Send, CheckCircle } from 'lucide-react';
import { getApiBaseUrl } from '../../utils/api';
import './FeedbackDrawer.css';

interface FeedbackDrawerProps {
  onClose: () => void;
}

type FeedbackCategory = 'bug' | 'feature' | 'idea' | 'other';

interface FeedbackSubmission {
  category: FeedbackCategory;
  message: string;
  user_id?: string;
  submitted_at: string;
}

export const FeedbackDrawer: React.FC<FeedbackDrawerProps> = ({ onClose }) => {
  const { user } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState<FeedbackCategory | null>(null);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Phase 1 (Feature Backlog): 4 canonical categories. The backend
  // normalizer accepts the legacy strings (problem|feature_idea|feedback)
  // for the rollout grace window, but new submissions use these.
  const categories = [
    {
      id: 'bug' as FeedbackCategory,
      title: 'Something Broken',
      description: 'Report a bug or unexpected behavior',
      icon: Bug,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200'
    },
    {
      id: 'feature' as FeedbackCategory,
      title: 'Feature Request',
      description: 'A specific feature you want us to build',
      icon: Lightbulb,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200'
    },
    {
      id: 'idea' as FeedbackCategory,
      title: 'Idea or Suggestion',
      description: 'A rough idea — we\'ll help shape it',
      icon: Sparkles,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200'
    },
    {
      id: 'other' as FeedbackCategory,
      title: 'General Feedback',
      description: 'Share your thoughts and experiences',
      icon: Heart,
      color: 'text-pink-600',
      bgColor: 'bg-pink-50',
      borderColor: 'border-pink-200'
    }
  ];

  const handleSubmit = async () => {
    if (!selectedCategory || !message.trim()) {
      console.log('❌ FEEDBACK: Missing category or message');
      return;
    }

    console.log('📝 FEEDBACK: Starting submission...');
    setIsSubmitting(true);
    
    const feedbackData: FeedbackSubmission = {
      category: selectedCategory,
      message: message.trim(),
      user_id: user?.id,
      submitted_at: new Date().toISOString()
    };

    console.log('📝 FEEDBACK: Data prepared:', JSON.stringify(feedbackData, null, 2));

    try {
      const API_BASE = getApiBaseUrl();
      const url = `${API_BASE}/feedback/`;
      
      console.log('📝 FEEDBACK: Submitting to:', url);
      console.log('📝 FEEDBACK: User ID:', user?.id);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(feedbackData)
      });

      console.log('📝 FEEDBACK: Response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('📝 FEEDBACK: Response data:', JSON.stringify(result, null, 2));
        
        if (result.success) {
          console.log('✅ FEEDBACK: Successfully submitted!');
          setIsSubmitted(true);
          // Auto-close after showing thank you message
          setTimeout(() => {
            onClose();
          }, 3000);
        } else {
          throw new Error(result.message || 'Failed to submit feedback');
        }
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('❌ FEEDBACK: Failed -', response.status, errorData);
        throw new Error(errorData.detail || `Failed to submit feedback (${response.status})`);
      }
    } catch (error) {
      console.error('❌ FEEDBACK: Submission error:', error);
      alert(error instanceof Error ? error.message : 'Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCategoryPlaceholder = (category: FeedbackCategory) => {
    switch (category) {
      case 'problem':
        return 'Describe what happened and what you expected to happen instead...';
      case 'feature_idea':
        return 'Tell us about your idea and how it would help you in your fitness journey...';
      case 'feedback':
        return 'Share your thoughts about Coach Cedar - what\'s working well, what could be better...';
      default:
        return 'Share your thoughts...';
    }
  };

  if (isSubmitted) {
    return (
      <div className="feedback-drawer">
        <div className="feedback-success">
          <CheckCircle size={56} style={{ color: '#1f6c4f' }} />
          <div className="feedback-success-title">Thank you</div>
          <div className="feedback-success-body">
            We read every message. Your feedback helps Coach Cedar improve for everyone.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="feedback-drawer">
      <div className="feedback-header">
        <div>
          <div className="feedback-title">Feedback</div>
          <div className="feedback-subtitle">Help us make Coach Cedar better</div>
        </div>
      </div>

      {/* Category Selection */}
      <div className="feedback-cards">
        {categories.map((category) => {
          const Icon = category.icon;
          const isSelected = selectedCategory === category.id;
          
          return (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`feedback-card ${isSelected ? 'selected' : ''}`}
              type="button"
            >
              <div className="feedback-card-row">
                <div className="feedback-card-icon" aria-hidden="true">
                  <Icon size={20} />
                </div>
                <div className="feedback-card-text">
                  <div className="feedback-card-title">{category.title}</div>
                  <div className="feedback-card-desc">{category.description}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Message Input */}
      {selectedCategory && (
        <div className="feedback-form">
          <div className="feedback-label">Your message</div>
          <textarea
            id="feedback-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={getCategoryPlaceholder(selectedCategory)}
            rows={6}
            className="feedback-textarea"
          />

          <button
            type="button"
            onClick={handleSubmit}
            disabled={!message.trim() || isSubmitting}
            className="feedback-submit"
          >
            <Send size={16} />
            {isSubmitting ? 'Sending…' : 'Submit'}
          </button>
        </div>
      )}
    </div>
  );
}; 